import {
    Amount,
    BaseInput,
    CoreFetch,
    CurrencyType,
    Invoice,
    InvoiceApi,
    InvoiceType,
    ProviderPatientStatement,
    IToastContextProps,
} from '@enter/core';
import { mdiChevronRight, mdiTagOutline } from '@mdi/js';
import React, { useEffect, useMemo, useState } from 'react';
import { IDialogContext } from '../statement-details.types';
import { StatementApplyToClaims } from './statement-apply-to-claims';
import {
    allocateAmountAcrossClaims,
    getPayableClaims,
    resolveTargetClaims,
    sortClaimsByOldestDos,
    StatementClaimOption,
} from './statement-apply-to-claims.utils';
import { formatLedgerMoney, parseLedgerAmount } from '../utils/statement-format.utils';
import { getBaseInvoiceAmount, getProviderDiscount } from './discount-adjustment.utils';
import * as S from './statement-ledger-dialog.styles';

type RecordDiscountDialogProps = {
    patientStatement?: ProviderPatientStatement;
    /** Apply to this single claim only — hides the "Apply to" section (used from claim details). */
    fixedClaim?: StatementClaimOption;
    /** The exact invoice to discount when `fixedClaim` is set — skips the provider invoice lookup. */
    fixedInvoice?: Invoice;
    /** Override how the item is posted (the support app uses its own endpoint). Defaults to the provider endpoint. */
    postInvoiceItem?: (invoiceId: string, name: string, amount: Amount) => Promise<unknown>;
    /** Receives the invoice the provider endpoint returns, so the caller can refresh it without a full reload. */
    onInvoiceUpdated?: (invoice: Invoice) => void;
    onClose: () => void;
    onSuccess: () => void;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
    equalsFilter: (term?: string | string[]) => string[] | undefined;
};

export const RecordDiscountDialog: React.FunctionComponent<RecordDiscountDialogProps> = (props) => {
    const {
        dialogContext,
        toastContext,
        equalsFilter,
        patientStatement,
        fixedClaim,
        fixedInvoice,
        postInvoiceItem,
        onInvoiceUpdated,
        onClose,
        onSuccess,
    } = props;

    const [selectedClaimIds, setSelectedClaimIds] = useState<Record<string, boolean>>({});
    const [memo, setMemo] = useState<string>('Patient Loyalty Adjustment');
    const [amount, setAmount] = useState<string>('');
    const [baseAmount, setBaseAmount] = useState<string>('0');
    const [endingAmount, setEndingAmount] = useState<string>('0');
    const [existingDiscount, setExistingDiscount] = useState<number>(0);
    const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const payableClaims = useMemo(() => getPayableClaims(patientStatement), [patientStatement]);

    // A claim opened from claim details is the only target. On a statement the user picks claims.
    const targetClaims = useMemo(() => {
        if (fixedClaim) {
            return [fixedClaim];
        }
        return resolveTargetClaims(payableClaims, 'manual', selectedClaimIds);
    }, [fixedClaim, payableClaims, selectedClaimIds]);

    const selectedBalanceTotal = targetClaims.reduce((sum, claim) => sum + claim.balance, 0);

    // The provider endpoint holds a single PROVIDER_ADJUSTMENT per claim, so posting replaces it.
    // The support endpoint (postInvoiceItem) adds an ENTER_ADJUSTMENT on top and replaces nothing.
    const isProviderMode = !postInvoiceItem;

    // Seeds base, the discount already on the invoice, and the form from the loaded invoice.
    const applyInvoiceAmounts = (base: number, existing: number): void => {
        setBaseAmount(base.toFixed(2));
        setExistingDiscount(existing);
        setAmount(existing > 0 ? existing.toFixed(2) : '');
        setEndingAmount((base - existing).toFixed(2));
    };

    const applyInvoice = (invoice: Invoice): void => {
        if (isProviderMode) {
            applyInvoiceAmounts(getBaseInvoiceAmount(invoice), getProviderDiscount(invoice));
        } else {
            applyInvoiceAmounts(parseLedgerAmount(invoice.amount?.amount), 0);
        }
    };

    useEffect(() => {
        // Set by the cleanup when the selection changes, so a slower response for a superseded
        // claim cannot overwrite the form of the claim that is now selected.
        let superseded = false;

        const loadInvoiceDetails = async (): Promise<void> => {
            if (targetClaims.length !== 1) {
                setIsLoadingInvoice(false);
                applyInvoiceAmounts(selectedBalanceTotal, 0);
                return;
            }

            if (fixedInvoice) {
                setIsLoadingInvoice(false);
                applyInvoice(fixedInvoice);
                return;
            }

            const claimId = targetClaims[0].id;
            setIsLoadingInvoice(true);
            try {
                const invoices = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoices({
                    filterClaimId: equalsFilter(claimId),
                    filterInvoiceType: equalsFilter(InvoiceType.PATIENT),
                });
                if (superseded) {
                    return;
                }

                if (!invoices || invoices.length === 0) {
                    applyInvoiceAmounts(targetClaims[0].balance, 0);
                    return;
                }

                applyInvoice(invoices[invoices.length - 1]);
            } catch (err) {
                if (superseded) {
                    return;
                }
                dialogContext.showError(err);
                applyInvoiceAmounts(targetClaims[0].balance, 0);
            } finally {
                // The newer selection owns the loading flag once this one is superseded.
                if (!superseded) {
                    setIsLoadingInvoice(false);
                }
            }
        };

        void loadInvoiceDetails();
        return () => {
            superseded = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when claim selection changes; amount handled separately
    }, [selectedClaimIds, selectedBalanceTotal, fixedInvoice, fixedClaim]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        const parsedAmount = parseLedgerAmount(amount);

        if (isProviderMode) {
            // 0.00 is how a provider removes the discount, so it only means something when one exists.
            const minimum = existingDiscount > 0 ? 0 : 0.01;
            if (!amount || parsedAmount < minimum) {
                newErrors.amount =
                    existingDiscount > 0
                        ? 'Discount amount is required (enter 0.00 to remove the discount)'
                        : 'Discount amount is required and must be greater than 0';
            }
        } else if (!amount || parsedAmount <= 0) {
            newErrors.amount = 'Discount amount is required and must be greater than 0';
        }

        if (!targetClaims.length) {
            newErrors.claims = payableClaims.length
                ? 'Select at least one claim'
                : 'This statement has no claims to apply a discount to.';
        }

        if (!memo || memo.trim() === '') {
            newErrors.memo = 'Discount type is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const postDiscountToClaim = async (claimId: string, discountAmount: number): Promise<void> => {
        let invoiceId = fixedInvoice?.id;
        if (!invoiceId) {
            const invoices = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoices({
                filterClaimId: equalsFilter(claimId),
                filterInvoiceType: equalsFilter(InvoiceType.PATIENT),
            });

            if (!invoices || invoices.length === 0) {
                throw new Error(`No patient invoice found for claim ${claimId}`);
            }

            invoiceId = invoices[invoices.length - 1].id;
        }
        const finalAmount = discountAmount > 0 ? -discountAmount : discountAmount;
        const itemAmount: Amount = { amount: finalAmount.toString(), currency: CurrencyType.USD };

        if (postInvoiceItem) {
            await postInvoiceItem(invoiceId!, memo.trim(), itemAmount);
            return;
        }

        const updated = await CoreFetch<InvoiceApi>(InvoiceApi).postProviderInvoiceItem({
            id: invoiceId!,
            createBillingInvoiceItemPayload: {
                name: memo.trim(),
                amount: itemAmount,
            },
        });
        if (updated) {
            onInvoiceUpdated?.(updated);
        }
    };

    const handleSubmit = async (): Promise<void> => {
        // The form still holds the previous claim's values until the current one has loaded.
        if (isLoadingInvoice || !validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const parsedAmount = parseLedgerAmount(amount);
            const orderedClaims = [...targetClaims].sort(sortClaimsByOldestDos);
            // Allocation drops a zero amount, but a zero is the removal and must still be posted.
            const isRemoval = isProviderMode && parsedAmount === 0;
            const allocations = isRemoval
                ? [{ claimId: orderedClaims[0].id, amount: 0 }]
                : allocateAmountAcrossClaims(orderedClaims, parsedAmount);

            if (!allocations.length) {
                throw new Error('Unable to allocate discount to selected claims');
            }

            for (const allocation of allocations) {
                await postDiscountToClaim(allocation.claimId, allocation.amount);
            }

            toastContext.notify?.(
                isRemoval
                    ? 'Discount was removed successfully.'
                    : allocations.length > 1
                    ? `Discount was recorded across ${allocations.length} claims.`
                    : 'Discount was recorded successfully.',
                'right',
            );
            onSuccess();
        } catch (err) {
            dialogContext.showError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;
        setAmount(next);
        const base = parseLedgerAmount(baseAmount);
        const discount = parseLedgerAmount(next);
        setEndingAmount((base - discount).toFixed(2));
    };

    const handleEndingAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;
        setEndingAmount(next);
        const base = parseLedgerAmount(baseAmount);
        const ending = parseLedgerAmount(next);
        setAmount((base - ending).toFixed(2));
    };

    const selectClaim = (claimId: string): void => {
        setSelectedClaimIds({ [claimId]: true });
    };

    return (
        <S.DialogBody>
            <S.Header>
                <S.HeaderIcon path={mdiTagOutline} size={0.9} />
                <S.HeaderTitle>{isProviderMode ? 'Set claim discount' : 'Record discount'}</S.HeaderTitle>
            </S.Header>

            <S.FormSection>
                <S.Label>Amount</S.Label>
                <S.AmountField>
                    <S.AmountPrefix>$</S.AmountPrefix>
                    <BaseInput
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={amount}
                        onChange={handleAmountChange}
                        disabled={isLoadingInvoice}
                        disableLatin1Validation
                    />
                </S.AmountField>
                {errors.amount && <S.ErrorText>{errors.amount}</S.ErrorText>}
                {isProviderMode && existingDiscount > 0 && (
                    <S.FifoNote>
                        This claim already has a {formatLedgerMoney(existingDiscount)} discount. The amount above
                        replaces it; enter 0.00 to remove it.
                    </S.FifoNote>
                )}

                <S.CollapseToggle
                    type="button"
                    variant="unstyled"
                    onClick={() => setShowInvoiceDetails(open => !open)}>
                    <S.CollapseIcon path={mdiChevronRight} size={0.65} open={showInvoiceDetails} />
                    Invoice amount details
                    {isLoadingInvoice ? ' (loading…)' : ''}
                </S.CollapseToggle>

                {showInvoiceDetails && (
                    <S.CollapsePanel>
                        <div>
                            <S.Label>Base Invoice Amount</S.Label>
                            <BaseInput type="number" value={baseAmount} placeholder="0.00" readOnly />
                        </div>
                        <div>
                            <S.Label>Ending Invoice Amount</S.Label>
                            <BaseInput
                                type="number"
                                value={endingAmount}
                                onChange={handleEndingAmountChange}
                                disabled={isLoadingInvoice}
                                placeholder="0.00"
                            />
                        </div>
                    </S.CollapsePanel>
                )}
            </S.FormSection>

            {!fixedClaim && (
                <StatementApplyToClaims
                    applyMode="manual"
                    openClaims={payableClaims}
                    selectableClaims={payableClaims}
                    selectedClaimIds={selectedClaimIds}
                    onToggleClaim={selectClaim}
                    targetClaims={targetClaims}
                    errorMessage={errors.claims}
                    selectionOnly
                />
            )}

            <S.FormSection>
                <S.Label>Discount type</S.Label>
                <BaseInput
                    value={memo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemo(e.target.value)}
                    errorMessage={errors.memo}
                    placeholder="Enter discount type"
                    required
                />
            </S.FormSection>

            <S.ButtonGroup
                changed={true}
                useNotify={false}
                onCancel={onClose}
                onSave={handleSubmit}
                saveLabel={isSubmitting ? 'Applying...' : 'Apply discount'}
                saveNotice="Discount recorded successfully"
            />
        </S.DialogBody>
    );
};
